import { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { useConversation } from "@elevenlabs/react";

const AGENT_ID = import.meta.env.VITE_ELEVENLABS_AGENT_ID;

type AssistantState = "idle" | "connecting" | "listening" | "speaking";

interface VoiceAssistantContextType {
  isConnecting: boolean;
  isCancelling: boolean;
  isActive: boolean;
  isSpeaking: boolean;
  toggleConversation: () => Promise<void>;
  endConversation: () => Promise<void>;
  assistantState: AssistantState;
  error: string | null;
}

const VoiceAssistantContext = createContext<VoiceAssistantContextType | null>(null);

export function VoiceAssistantProvider({ children }: { children: ReactNode }) {
  const [isActive, setIsActive] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const { toast } = useToast();
  
  // Use a ref to track if we initiated the connection to prevent double-firing
  const isInitiatingRef = useRef(false);
  // AbortController to cancel in-flight connection requests
  const abortControllerRef = useRef<AbortController | null>(null);

  const conversation = useConversation({
    onConnect: () => {
      console.log("Connected to AI assistant");
      setIsActive(true);
      isInitiatingRef.current = false;
    },
    onDisconnect: () => {
      console.log("Disconnected from AI assistant");
      setIsActive(false);
      isInitiatingRef.current = false;
      setIsCancelling(false);
    },
    onError: (error) => {
      console.error("Conversation error:", error);
      setIsActive(false);
      isInitiatingRef.current = false;
      setIsCancelling(false);
      
      // Ignore abort errors
      // Use looser check for AbortError to be safe across environments
      const isAbort = 
        (typeof error === 'object' && error !== null && 'name' in error && (error as Error).name === 'AbortError') ||
        (typeof error === 'string' && (error.includes('AbortError') || error.includes('Aborted')));

      if (isAbort) return;

      toast({
        variant: "destructive",
        title: "Connection Error",
        description: error instanceof Error ? error.message : "Failed to connect to voice assistant",
      });
    },
    onModeChange: (mode) => {
      console.log("Mode changed:", mode);
    }
  });

  const { status, isSpeaking } = conversation;
  
  // Connect explicit "isConnecting" state to our local initiating ref + library status
  // This ensures UI shows "Connecting..." immediately when we start our async process
  const isConnecting = isInitiatingRef.current || status === "connecting";

  const getAssistantState = (): AssistantState => {
    if (isConnecting) return "connecting";
    if (status === "connected") {
      return isSpeaking ? "speaking" : "listening";
    }
    return "idle";
  };

  const startConversation = useCallback(async () => {
    if (isInitiatingRef.current || status === "connected" || status === "connecting") return;
    
    // Create new abort controller for this attempt
    abortControllerRef.current = new AbortController();
    isInitiatingRef.current = true;
    setIsCancelling(false); // Reset cancelling state on new start
    
    // Force update to show connecting state immediately
    // Note: Since isConnecting is derived from ref, we need to trigger re-render if needed, 
    // but typically the state change in 'conversation' or parent re-renders will handle it.
    // For safety, we can rely on setIsActive(false) -> true later or just rely on the component using the hook re-rendering.
    // To be safe, let's just proceed.

    try {
      // 1. Get Microphone (cancellable by just ignoring result if aborted)
      await navigator.mediaDevices.getUserMedia({ audio: true });
      
      if (abortControllerRef.current?.signal.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      // 2. Fetch Token
      const functionUrl = `${import.meta.env.VITE_SUPABASE_FUNCTION_URL}`; 
      
      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch token: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      if (abortControllerRef.current?.signal.aborted) {
         throw new DOMException('Aborted', 'AbortError');
      }

      // 3. Start Session
      await conversation.startSession({
        conversationToken: data.token,
      });

    } catch (err) {
      // Only log if not an abort, to avoid noise
      const isAbort = 
        (err instanceof Error && err.name === 'AbortError') || 
        (err instanceof DOMException && err.name === 'AbortError') ||
        (typeof err === 'string' && (err.includes('AbortError') || err.includes('aborted')));

      if (!isAbort) {
        console.error("Failed to start conversation:", err);
      }
      
      isInitiatingRef.current = false;
      
      const errorMessage = err instanceof Error ? err.message : "Unknown error";

      if (!isAbort) {
        if (errorMessage.includes("Permission denied") || errorMessage.includes("NotAllowedError")) {
           toast({
            variant: "destructive",
            title: "Microphone Access Denied",
            description: "Please allow microphone access to use the voice assistant.",
          });
        } else {
          toast({
            variant: "destructive",
            title: "Connection Failed",
            description: "Could not start the voice session. Please try again.",
          });
        }
      }
    }
  }, [conversation, toast, status]);

  const endConversation = useCallback(async () => {
    setIsCancelling(true);
    
    // 1. Cancel any in-flight setup
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // 2. Reset initiating flag immediately so UI reflects "canceling/idle"
    isInitiatingRef.current = false;

    // 3. End actual elevenlabs session if active
    if (status === "connected" || status === "connecting") {
      try {
        await conversation.endSession();
      } catch (error) {
        console.error("Error ending session:", error);
      }
    }
    
    // Safety timeout to reset cancelling state if onDisconnect doesn't fire
    setTimeout(() => setIsCancelling(false), 2000);
    
  }, [conversation, status]);

  const toggleConversation = useCallback(async () => {
    if (isCancelling) return; // Prevent toggling while cancelling

    // If we are initiating (our local state) OR library thinks we are connected/connecting
    if (isInitiatingRef.current || status === "connected" || status === "connecting") {
      await endConversation();
    } else {
      await startConversation();
    }
  }, [status, startConversation, endConversation, isCancelling]);

  // Handle body scroll lock
  useEffect(() => {
    if (isActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isActive]);

  return (
    <VoiceAssistantContext.Provider
      value={{
        isConnecting,
        isCancelling,
        isActive: status === "connected" || status === "connecting" || isInitiatingRef.current,
        isSpeaking,
        toggleConversation,
        endConversation,
        assistantState: getAssistantState(),
        error: null, 
      }}
    >
      {children}
    </VoiceAssistantContext.Provider>
  );
}

export function useVoiceAssistant() {
  const context = useContext(VoiceAssistantContext);
  if (!context) {
    throw new Error("useVoiceAssistant must be used within a VoiceAssistantProvider");
  }
  return context;
}
